export interface StoredTokens {
  accessToken: string;
  refreshToken?: string;
  accessTokenExpiresAt?: string;
  accessTokenExpiresAtMs?: number;
  refreshTokenExpiresAt?: string;
  refreshTokenExpiresAtMs?: number;
}

export const ACCESS_TOKEN_KEY = "onboard.accessToken";
export const REFRESH_TOKEN_KEY = "onboard.refreshToken";
export const ACCESS_TOKEN_EXPIRES_AT_KEY = "onboard.accessTokenExpiresAt";
export const ACCESS_TOKEN_EXPIRES_AT_MS_KEY = "onboard.accessTokenExpiresAtMs";
export const REFRESH_TOKEN_EXPIRES_AT_KEY = "onboard.refreshTokenExpiresAt";
export const REFRESH_TOKEN_EXPIRES_AT_MS_KEY =
  "onboard.refreshTokenExpiresAtMs";
export const AUTH_HOST_KEY = "onboard.authHost";
export const ADMIN_SESSION_MARKER_KEY = "onboard.adminSession";
export const ADMIN_LAST_ACTIVITY_AT_KEY = "onboard.adminLastActivityAt";
export const AUTH_SYNC_STORAGE_KEY = "onboard.authSync";
export const TOKENS_UPDATED_EVENT = "auth:tokens";
export const AUTH_LOGOUT_EVENT = "auth:logout";

type AuthSyncType = "tokens" | "logout";

const AUTH_SYNC_CHANNEL_NAME = "onboard-auth";

const resolveCurrentHost = () =>
  typeof window === "undefined" ? null : window.location.host || null;

const readAccessStorage = () => {
  if (typeof sessionStorage === "undefined") {
    return null;
  }
  return sessionStorage;
};

const dispatchWindowEvent = (eventName: string) => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(eventName));
  }
};

const publishSync = (type: AuthSyncType) => {
  if (typeof window === "undefined") {
    return;
  }

  const payload = JSON.stringify({
    type,
    ts: Date.now(),
    host: resolveCurrentHost(),
  });

  if ("BroadcastChannel" in window) {
    const channel = new BroadcastChannel(AUTH_SYNC_CHANNEL_NAME);
    channel.postMessage(payload);
    channel.close();
  }

  try {
    localStorage.setItem(AUTH_SYNC_STORAGE_KEY, payload);
  } catch (_error) {
    // ignore best-effort cross-tab sync failures
  }
};

const clearStoredTokens = () => {
  const storage = readAccessStorage();
  if (!storage) {
    return;
  }
  storage.removeItem(ACCESS_TOKEN_KEY);
  storage.removeItem(ACCESS_TOKEN_EXPIRES_AT_KEY);
  storage.removeItem(ACCESS_TOKEN_EXPIRES_AT_MS_KEY);
  storage.removeItem(REFRESH_TOKEN_KEY);
  storage.removeItem(REFRESH_TOKEN_EXPIRES_AT_KEY);
  storage.removeItem(REFRESH_TOKEN_EXPIRES_AT_MS_KEY);
  storage.removeItem(AUTH_HOST_KEY);
  storage.removeItem(ADMIN_SESSION_MARKER_KEY);
  storage.removeItem(ADMIN_LAST_ACTIVITY_AT_KEY);
};

export const subscribeToAuthSync = (onSync: (type: AuthSyncType) => void) => {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  const handleStorage = (event: StorageEvent) => {
    if (event.storageArea !== localStorage) return;
    if (event.key !== AUTH_SYNC_STORAGE_KEY || !event.newValue) return;
    try {
      const parsed = JSON.parse(event.newValue) as { type?: AuthSyncType };
      if (parsed.type === "tokens" || parsed.type === "logout") {
        onSync(parsed.type);
      }
    } catch (_error) {
      // ignore malformed sync payloads
    }
  };

  let channel: BroadcastChannel | null = null;
  if ("BroadcastChannel" in window) {
    channel = new BroadcastChannel(AUTH_SYNC_CHANNEL_NAME);
    channel.onmessage = (event) => {
      try {
        const parsed = JSON.parse(String(event.data)) as {
          type?: AuthSyncType;
        };
        if (parsed.type === "tokens" || parsed.type === "logout") {
          onSync(parsed.type);
        }
      } catch (_error) {
        // ignore malformed sync payloads
      }
    };
  }

  window.addEventListener("storage", handleStorage);
  return () => {
    window.removeEventListener("storage", handleStorage);
    channel?.close();
  };
};

export const authStorage = {
  markAdminSession(activityAt = Date.now()) {
    const storage = readAccessStorage();
    if (!storage) return;
    storage.setItem(ADMIN_SESSION_MARKER_KEY, "active");
    storage.setItem(ADMIN_LAST_ACTIVITY_AT_KEY, String(activityAt));
  },
  clearAdminSessionMarker() {
    const storage = readAccessStorage();
    if (!storage) return;
    storage.removeItem(ADMIN_SESSION_MARKER_KEY);
    storage.removeItem(ADMIN_LAST_ACTIVITY_AT_KEY);
  },
  hasAdminSessionMarker() {
    return readAccessStorage()?.getItem(ADMIN_SESSION_MARKER_KEY) === "active";
  },
  recordAdminActivity(activityAt = Date.now()) {
    const storage = readAccessStorage();
    if (!storage || storage.getItem(ADMIN_SESSION_MARKER_KEY) !== "active") {
      return;
    }
    storage.setItem(ADMIN_LAST_ACTIVITY_AT_KEY, String(activityAt));
  },
  getAdminLastActivityAt(): number | null {
    const raw = readAccessStorage()?.getItem(ADMIN_LAST_ACTIVITY_AT_KEY);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  },
  save(tokens: StoredTokens) {
    const storage = readAccessStorage();
    if (!storage) {
      return;
    }

    storage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
    storage.setItem(AUTH_HOST_KEY, resolveCurrentHost() ?? "");

    if (tokens.accessTokenExpiresAt) {
      storage.setItem(ACCESS_TOKEN_EXPIRES_AT_KEY, tokens.accessTokenExpiresAt);
    } else {
      storage.removeItem(ACCESS_TOKEN_EXPIRES_AT_KEY);
    }
    if (typeof tokens.accessTokenExpiresAtMs === "number") {
      storage.setItem(
        ACCESS_TOKEN_EXPIRES_AT_MS_KEY,
        String(tokens.accessTokenExpiresAtMs),
      );
    } else {
      storage.removeItem(ACCESS_TOKEN_EXPIRES_AT_MS_KEY);
    }

    storage.removeItem(REFRESH_TOKEN_KEY);
    storage.removeItem(REFRESH_TOKEN_EXPIRES_AT_KEY);
    storage.removeItem(REFRESH_TOKEN_EXPIRES_AT_MS_KEY);

    dispatchWindowEvent(TOKENS_UPDATED_EVENT);
    publishSync("tokens");
  },
  get(): StoredTokens | null {
    const storage = readAccessStorage();
    if (!storage) {
      return null;
    }

    const storedHost = storage.getItem(AUTH_HOST_KEY);
    const currentHost = resolveCurrentHost();
    if (
      storedHost &&
      currentHost &&
      storedHost.trim().length > 0 &&
      storedHost !== currentHost
    ) {
      clearStoredTokens();
      return null;
    }

    const accessToken = storage.getItem(ACCESS_TOKEN_KEY);
    if (!accessToken) {
      return null;
    }

    const accessTokenExpiresAt =
      storage.getItem(ACCESS_TOKEN_EXPIRES_AT_KEY) ?? undefined;
    const accessTokenExpiresAtMsRaw = storage.getItem(
      ACCESS_TOKEN_EXPIRES_AT_MS_KEY,
    );
    const accessTokenExpiresAtMs =
      accessTokenExpiresAtMsRaw &&
      !Number.isNaN(Number(accessTokenExpiresAtMsRaw))
        ? Number(accessTokenExpiresAtMsRaw)
        : undefined;

    return {
      accessToken,
      accessTokenExpiresAt,
      accessTokenExpiresAtMs,
    };
  },
  clear(options?: { broadcast?: boolean; emitLogout?: boolean }) {
    clearStoredTokens();
    if (options?.emitLogout !== false) {
      dispatchWindowEvent(AUTH_LOGOUT_EVENT);
    }
    if (options?.broadcast !== false) {
      publishSync("logout");
    }
  },
  updateAccessToken(
    accessToken: string,
    accessTokenExpiresAt?: string,
    accessTokenExpiresAtMs?: number,
  ) {
    this.save({
      accessToken,
      accessTokenExpiresAt,
      accessTokenExpiresAtMs,
    });
  },
};
